import LZString from 'lz-string';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { RevelioPageId } from '../../PageConfigProvider/types';
import {
  TALENT_DISCOVERY_HIDDEN_COLUMNS,
  TALENT_DISCOVERY_VALID_COLUMNS,
} from './talent-discovery-columns';
import { TD_DEFAULT_COLUMNS } from './utils/table-helpers';
import { migrateColumnNames } from './utils/talent-discovery-column-mappings';

const isValidColumns = (columns: unknown): columns is string[] => {
  if (!Array.isArray(columns)) {
    return false;
  }

  return columns.every((col) => typeof col === 'string');
};

const validateTalentDiscoveryColumns = (columns: string[]): string[] => {
  const validColumnSet = new Set(TALENT_DISCOVERY_VALID_COLUMNS);
  const hiddenColumnSet = new Set(TALENT_DISCOVERY_HIDDEN_COLUMNS);

  return columns.filter(
    (col) => validColumnSet.has(col) && !hiddenColumnSet.has(col)
  );
};

const getDefaultColumns = (
  revelioPageId?: RevelioPageId,
  defaultColumns?: string[]
): string[] => {
  if (defaultColumns && defaultColumns.length > 0) {
    return defaultColumns;
  }

  return revelioPageId === RevelioPageId.TALENT_DISCOVERY
    ? TD_DEFAULT_COLUMNS
    : [];
};

const areArraysEqual = (left: string[], right: string[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => value === right[index]);
};

const getUniqueColumns = (columns: string[]): string[] => {
  const seen = new Set<string>();
  return columns.filter((column) => {
    if (seen.has(column)) {
      return false;
    }
    seen.add(column);
    return true;
  });
};

const filterValidColumns = (
  columns: string[],
  validColumnNames?: string[]
): string[] => {
  if (!validColumnNames || validColumnNames.length === 0) {
    return columns;
  }

  const validSet = new Set(validColumnNames);
  return columns.filter((col) => validSet.has(col));
};

const getFallbackColumns = (
  revelioPageId?: RevelioPageId,
  defaultColumns?: string[],
  validColumnNames?: string[]
): string[] => {
  const defaults = getDefaultColumns(revelioPageId, defaultColumns);
  const filteredDefaults = filterValidColumns(defaults, validColumnNames);

  if (validColumnNames && validColumnNames.length > 0) {
    return filteredDefaults;
  }

  return defaults;
};

const getColumnsFromCompressed = (
  compressed: string,
  source: 'url' | 'storage'
): string[] | undefined => {
  const decompressed =
    source === 'url'
      ? LZString.decompressFromEncodedURIComponent(compressed)
      : LZString.decompressFromUTF16(compressed);

  if (!decompressed) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(decompressed);
    if (!isValidColumns(parsed) || parsed.length === 0) {
      return undefined;
    }

    return parsed;
    // biome-ignore lint/correctness/noUnusedVariables: migrated from develop
  } catch (error) {
    return undefined;
  }
};

type ResolveInitialColumnsArgs = {
  locationSearch: string;
  storageKey: string;
  revelioPageId?: RevelioPageId;
  defaultColumns?: string[];
  validColumnNames?: string[];
};

export const resolveInitialTableColumns = ({
  locationSearch,
  storageKey,
  revelioPageId,
  defaultColumns,
  validColumnNames,
}: ResolveInitialColumnsArgs): string[] => {
  const params = new URLSearchParams(locationSearch);
  const columnsParam = params.get('columns');
  const fallbackSelection = getFallbackColumns(
    revelioPageId,
    defaultColumns,
    validColumnNames
  );
  const storageColumnsRaw = sessionStorage.getItem(storageKey);
  const fromUrl = columnsParam
    ? getColumnsFromCompressed(columnsParam, 'url')
    : undefined;
  const fromStorage = storageColumnsRaw
    ? getColumnsFromCompressed(storageColumnsRaw, 'storage')
    : undefined;
  const resolvedColumns = fromUrl ?? fromStorage;

  if (!resolvedColumns) {
    return getUniqueColumns(fallbackSelection);
  }

  const uniqueResolvedColumns = getUniqueColumns(resolvedColumns);

  if (revelioPageId === RevelioPageId.TALENT_DISCOVERY) {
    const processedColumns = migrateColumnNames(uniqueResolvedColumns);
    const validatedColumns = validateTalentDiscoveryColumns(processedColumns);
    const fallback = getDefaultColumns(revelioPageId, defaultColumns);

    if (validatedColumns.length > 0) {
      return getUniqueColumns(validatedColumns);
    }

    return getUniqueColumns(fallback);
  }

  const filteredColumns = filterValidColumns(
    uniqueResolvedColumns,
    validColumnNames
  );

  if (filteredColumns.length > 0) {
    return getUniqueColumns(filteredColumns);
  }

  if (fallbackSelection.length > 0) {
    return getUniqueColumns(fallbackSelection);
  }

  return [];
};

type UseTableColumnSyncOptions = {
  skipInitialHydration?: boolean;
};

export const useTableColumnSync = (
  selectedColumns: string[],
  setSelectedColumns: (cols: string[]) => void,
  revelioPageId?: RevelioPageId,
  defaultColumns?: string[],
  validColumnNames?: string[],
  options?: UseTableColumnSyncOptions
) => {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const storageKey = `columns_${revelioPageId ?? 'custom_dash'}`;
  const { skipInitialHydration = false } = options || {};

  // Load from URL on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const columnsParam = params.get('columns');
    const fromUrl = columnsParam
      ? getColumnsFromCompressed(columnsParam, 'url')
      : undefined;

    const shouldRemoveInvalidUrl = !fromUrl && Boolean(columnsParam);

    if (shouldRemoveInvalidUrl) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('columns');
          return next;
        },
        { replace: true }
      );
    }

    if (skipInitialHydration) {
      return;
    }
    setSelectedColumns(
      resolveInitialTableColumns({
        locationSearch: location.search,
        storageKey,
        revelioPageId,
        defaultColumns,
        validColumnNames,
      })
    );
  }, []);

  // Sync to URL when columns change
  useEffect(() => {
    const uniqueSelectedColumns = getUniqueColumns(selectedColumns);
    const compressed =
      uniqueSelectedColumns.length > 0
        ? LZString.compressToEncodedURIComponent(
            JSON.stringify(uniqueSelectedColumns)
          )
        : undefined;

    if (uniqueSelectedColumns.length === 0) {
      sessionStorage.removeItem(storageKey);
    } else {
      sessionStorage.setItem(
        storageKey,
        LZString.compressToUTF16(JSON.stringify(uniqueSelectedColumns))
      );
    }

    const matchesCurrentUrl = (() => {
      const params = new URLSearchParams(location.search);
      if (!compressed) {
        return !params.has('columns');
      }
      return params.get('columns') === compressed;
    })();

    if (matchesCurrentUrl) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!compressed) {
          next.delete('columns');
        } else {
          next.set('columns', compressed);
        }
        return next;
      },
      { replace: true }
    );
  }, [selectedColumns, location.search, setSearchParams, storageKey]);

  useEffect(() => {
    if (!validColumnNames || validColumnNames.length === 0) {
      return;
    }

    const filtered = filterValidColumns(selectedColumns, validColumnNames);
    const fallbackSelection = getFallbackColumns(
      revelioPageId,
      defaultColumns,
      validColumnNames
    );
    const nextColumns = (() => {
      if (filtered.length > 0) {
        return filtered;
      }

      if (fallbackSelection.length > 0) {
        return fallbackSelection;
      }

      return [];
    })();

    const uniqueSelectedColumns = getUniqueColumns(selectedColumns);
    const uniqueNextColumns = getUniqueColumns(nextColumns);

    if (areArraysEqual(uniqueSelectedColumns, uniqueNextColumns)) {
      return;
    }

    setSelectedColumns(uniqueNextColumns);
  }, [
    selectedColumns,
    validColumnNames,
    revelioPageId,
    defaultColumns,
    setSelectedColumns,
  ]);
};
